import type { VehicleCategoryDataVehicleCategoryEnum } from '~/api/prebooking'
import type { InitOverrideFunction } from '~/api/pricing-offers'
import type { ApiV2OffersPost200Response, Offer } from '~/api/pricing-offers/models'
import type { City } from '~/composables/city'
import type { TranslationKey } from '~/composables/translate/translations'
import type { OfferLoadStatus } from '~/types/offer'
import { checkIsWebView } from '@camelot/webview-utils'
import * as Sentry from '@sentry/vue'
import { useDebounceFn } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { OffersApi } from '~/api/pricing-offers'
import { ResponseError } from '~/api/pricing-offers/runtime'
import { mapPaymentType, resolveAccountType } from '~/composables/offer-utils'
import { toRentalInstant } from '~/composables/prebooking-date'
import { translate, useTranslations } from '~/composables/translate/translations'
import { useAppStore } from '~/stores/appstore'
import { useDateTimeStore } from '~/stores/datetime'
import { useExtrasStore } from '~/stores/extras'
import { useLocationStore } from '~/stores/location'
import { usePaymentProfileStore } from '~/stores/payment-profile'
import { useReferralStore } from '~/stores/referral'
import { useStepStore } from '~/stores/step'
import { useVehiclesStore } from '~/stores/vehicles'

const offersApi = new OffersApi()

// 4xx responses (a 404 means no offer was found for the timeframe) map to the
// "no offers" state; everything else (5xx, network) maps to the generic error state.
function classifyOfferError(error: unknown): OfferLoadStatus {
  if (error instanceof ResponseError && error.response.status >= 400 && error.response.status < 500) {
    return 'no-offers'
  }
  return 'error'
}

export const useOfferStore = defineStore('offer', () => {
  const possibleOffers = ref<Offer[]>([])
  const selectedOffer = ref<Offer | undefined>()
  const blurNotSelectedVehicles = ref<boolean>()
  const metaSelectedBuildSeries = ref<VehicleCategoryDataVehicleCategoryEnum | undefined>(undefined)
  const offerLoadStatus = ref<OfferLoadStatus>('idle')

  const locationStore = useLocationStore()
  const vehiclesConfigurationStore = useVehiclesStore()
  const dateTimeStore = useDateTimeStore()
  const referralStore = useReferralStore()

  const startLoadingOffers = ref<boolean>(false)
  const loadingOffers = ref<boolean>(false)

  const getFormattedPricePerDay = computed<string | undefined>(() => selectedOffer.value
    ? formattedOfferPricePerDay(selectedOffer.value, useTranslations().language.value)
    : undefined,
  )

  const getCurrentBuildSeriesTranslation = computed<string | undefined>(() => {
    if (selectedOffer.value?.buildSeries === undefined) {
      return undefined
    }
    return translate(`pre_${selectedOffer.value.buildSeries.toUpperCase()}` as TranslationKey)
  })

  const getOfferDescription = computed<string | undefined>(() => {
    if (!selectedOffer.value) {
      return undefined
    }

    if (selectedOffer.value.mileageIncluded) {
      return translate('mileage_included')
    }

    return selectedOffer.value.description
  })

  // TODO:
  //  here we set a timeout of 300ms to let the vehicle store loading new vehicle configuration
  //  otherwise we will load twice the offer (before and after the vehicle configuration has been loaded)
  //  and pricing won't be happy :D :D
  //  we need to have a loading boolean on the vehicle store to block this one also  + a mechanism to not trigger that directly when the start date changes
  const debouncedLoadPossibleOffers = useDebounceFn(loadPossibleOffers, 300)

  watchEffect(() => {
    let buildSeries: string[] = []
    if (referralStore.isMetaFlow && metaSelectedBuildSeries.value) {
      // For meta flow, use only the selected build series
      const selectedBuildSeries = vehiclesConfigurationStore.getBuildSeries(metaSelectedBuildSeries.value)
      if (selectedBuildSeries) {
        buildSeries = [selectedBuildSeries]
      }
    }
    else {
      // For non-meta flow, use all build series
      buildSeries = vehiclesConfigurationStore.getAllBuildSeries()
    }

    if (buildSeries.length === 0) {
      return // Exit the watchEffect if no build series are available
    }

    debouncedLoadPossibleOffers(
      useTranslations().language.value,
      locationStore.city,
      startLoadingOffers.value,
      locationStore.isValid && dateTimeStore.areDatesValid,
      buildSeries,
      dateTimeStore.startDate.date,
      dateTimeStore.endDate.date,
    )
      .catch((error) => {
        Sentry.captureMessage(error)
        console.error('Error while fetching the offers', error)
        startLoadingOffers.value = false
        offerLoadStatus.value = classifyOfferError(error)
      })
      .finally(() => {
        loadingOffers.value = false
      })
  })

  async function loadPossibleOffers(
    language: string,
    city: City,
    startLoadingOffers: boolean,
    isValid: boolean,
    buildSeries: string[],
    startDate?: Date,
    endDate?: Date,
  ): Promise<void> {
    if (buildSeries.length === 0) {
      // console.debug('Vehicles configuration have not yet been loaded')
      return Promise.resolve()
    }

    if (startLoadingOffers && isValid) {
      loadingOffers.value = true
      offerLoadStatus.value = 'idle'

      const request = {
        buildSeries,
        pickupAt: toRentalInstant(startDate!),
        dropoffAt: toRentalInstant(endDate!),
        location: city.id,
      }

      let response: ApiV2OffersPost200Response

      const metaOffersEnabled = useRuntimeConfig().public.META_OFFERS_ENABLED
      const partner = referralStore.isMetaFlow && metaOffersEnabled ? 'meta' : undefined

      if (useAppStore().accessToken !== '') {
        const token = useAppStore().accessToken
        const selectedProfile = usePaymentProfileStore().selectedPaymentProfile
        const authenticatedRequest = {
          ...request,
          paymentProfileId: selectedProfile?.paymentProfileId,
          profileType: selectedProfile?.type,
          paymentType: mapPaymentType(selectedProfile?.payment?.type),
          accountType: resolveAccountType(selectedProfile),
        }
        const authorizationOverride: InitOverrideFunction = async (context) => {
          return {
            ...context.init,
            headers: {
              ...context.init.headers,
              Authorization: `Bearer ${token}`,
            },
          }
        }
        response = await offersApi.apiV2OffersPost(
          {
            acceptLanguage: language,
            apiV2OffersPostRequest: authenticatedRequest,
            partner,
          },
          authorizationOverride,
        )
      }
      else {
        response = await offersApi.apiV2OffersPost(
          {
            acceptLanguage: language,
            apiV2OffersPostRequest: request,
            partner,
          },
        )
      }

      possibleOffers.value = (response.offers ?? []).map(offer => ({ ...offer }))
      offerLoadStatus.value = possibleOffers.value.length > 0 ? 'idle' : 'no-offers'
      if (selectedOffer.value) {
        const previousBuildSeries = selectedOffer.value.buildSeries
        const previousCategory = selectedOffer.value.carCategory

        selectedOffer.value = possibleOffers.value
          .find((offer: Offer) => offer.buildSeries === previousBuildSeries)
          ?? possibleOffers.value.find((offer: Offer) => offer.carCategory === previousCategory)
      }
    }
    else {
      // console.debug(`No offer loaded. buildSeries?.length: ${buildSeries?.length}, startLoadingOffers: ${startLoadingOffers}, startDate: ${startDate}, endDate: ${endDate}`)
      resetOffers()
      return Promise.resolve()
    }
  }

  function resetOffers() {
    possibleOffers.value = []
    if (selectedOffer.value) {
      const stepStore = useStepStore()
      // Don't reset an already-made offer selection once the user has progressed
      // past the category page — a transient address validation failure or similar
      // should not wipe the user's chosen offer mid-flow.
      const pastCategoryStep = stepStore.currentStepPath && stepStore.currentStepPath !== 'category'
      if (pastCategoryStep) {
        return
      }
      selectedOffer.value = undefined
      useExtrasStore().reset()
      if (!checkIsWebView(window)) {
        stepStore.reset()
      }
    }
  }

  async function reloadOffers() {
    const buildSeries = referralStore.isMetaFlow && metaSelectedBuildSeries.value
      ? [vehiclesConfigurationStore.getBuildSeries(metaSelectedBuildSeries.value)].filter(Boolean) as string[]
      : vehiclesConfigurationStore.getAllBuildSeries()

    if (buildSeries.length === 0) {
      return
    }

    loadingOffers.value = true

    try {
      await loadPossibleOffers(
        useTranslations().language.value,
        locationStore.city,
        true,
        locationStore.isValid && dateTimeStore.areDatesValid,
        buildSeries,
        dateTimeStore.startDate.date,
        dateTimeStore.endDate.date,
      )
    }
    catch (error) {
      Sentry.captureException(error)
      console.error('Error while reloading offers', error)
      offerLoadStatus.value = classifyOfferError(error)
    }
    finally {
      loadingOffers.value = false
    }
  }

  const category = computed<VehicleCategoryDataVehicleCategoryEnum | undefined>(() =>
    selectedOffer.value?.buildSeries
      ? vehiclesConfigurationStore.getCategory(selectedOffer.value.buildSeries)
      : undefined,
  )

  const allBuildSeries = computed<string[]>(() =>
    selectedOffer.value?.buildSeries
      ? vehiclesConfigurationStore.getLinkedBuildSeries(selectedOffer.value.buildSeries)
      : [],
  )

  return {
    startLoadingOffers,
    loadingOffers,
    selectedOffer,
    possibleOffers,
    offerLoadStatus: readonly(offerLoadStatus),
    category,
    allBuildSeries,
    blurNotSelectedVehicles,
    getFormattedPricePerDay,
    getCurrentBuildSeriesTranslation,
    getOfferDescription,
    metaSelectedBuildSeries,
    reloadOffers,
  }
})
